/* Field park: the file answer sits in the field anatomy until the upload
 * round's file field (field-wrapper.md, "Retiring Field"). */
import { Field } from '@main/complykit';
import { FileField as FileFieldComponent } from '@main/dynamic-form';
import { VendorQuestionnaireFormAnswerValue } from '@main/graphql/client-scalars';
import { FileInputProps, FileUpload, useDownloadStorageFile, useEagerFileUpload } from '@main/ui';
import { createContext, useContext } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NotAnswered } from './not-answered';

export type FileFieldOverrides = FileInputProps & {
  readonly answerSupportFiles?: FileInputProps;
  readonly questionSupportingFile?: FileInputProps;
};

export const FileFieldContext = createContext<FileFieldOverrides>({});

export const FileField: FileFieldComponent = ({ config, state, labelId }) => {
  const { t } = useTranslation();
  const { control, setError, clearErrors } = useFormContext();
  const { field } = useController({ name: config.name, control, rules: config.validation });
  const {
    answerSupportFiles: _answerSupportFiles,
    questionSupportingFile: _questionSupportingFile,
    ...filesProps
  } = useContext(FileFieldContext);
  const onFileDownload = useDownloadStorageFile();
  const canUpload = !state.isDisabled && !state.isReadOnly;
  const upload = useEagerFileUpload({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    file: field.value?.file,
    onFileDownload,
    onFileUpload: canUpload
      ? async ({ file, error }) => {
          if (error) {
            setError(field.name, {
              type: 'file-upload-error',
              message: error.message || t('questionnaires.questions.uploadFailed'),
            });
          } else {
            clearErrors(field.name);
            field.onChange({ file } satisfies VendorQuestionnaireFormAnswerValue);
          }
        }
      : undefined,
    onFileDelete: canUpload
      ? async () => {
          clearErrors(field.name);
          field.onChange(null);
        }
      : undefined,
  });

  const name = labelId ? { 'aria-labelledby': labelId } : { label: config.label ?? config.name };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const hasFile = !!field.value?.file;

  return (
    // A file input cannot carry aria-required: the question's mark is read instead.
    <Field
      {...name}
      isReadOnly={state.isReadOnly}
      isDisabled={state.isDisabled}
      error={state.error}
    >
      {canUpload ? (
        (fieldControl) => (
          <FileUpload {...upload} {...filesProps}>
            <FileUpload.Files />
            <FileUpload.Dropzone inputProps={{ ...fieldControl, accept: config.accepts }} />
          </FileUpload>
        )
      ) : (
        <FileUpload {...upload} {...filesProps} isReadOnly>
          <FileUpload.Files />
          {!hasFile && <NotAnswered />}
        </FileUpload>
      )}
    </Field>
  );
};

FileField.resolveReadableValues = ({ config }) => {
  if (!config.value?.file) {
    return [];
  }

  return [`${config.value.file.id} - ${config.value.file.name ?? ''}`];
};
